/**
 * AS REGRAS DO HQ-LAB, VISTAS DO PAINEL — T17.
 *
 * *"todas as regras do hqlab devem ser possiveis editar elas (como restrição de palavras ou
 * fluxos) num painel onde um perfil com autorização administrativa consiga mexer. Nada deve ta
 * chumbado no código. Tudo deve está num banco de dados e que possa ser editado pelo painel
 * administrativo"*
 *
 * ── O QUE MORA AQUI, E POR QUE SEPARADO DA TELA ─────────────────────────────────────────────
 *
 * A forma do documento no banco, as edições de lista, e **o resumo do que mudou**. Tudo função
 * pura: a tela monta e mostra, e nenhuma decisão sobre regra de conteúdo depende de um clique
 * ter acontecido.
 *
 * ── O RESUMO DO QUE MUDOU É A PEÇA MAIS IMPORTANTE DESTE ARQUIVO ────────────────────────────
 *
 * Tirar as regras do código resolve o problema do operador e cria outro: o que antes passava por
 * commit, revisão e deploy agora passa por um formulário. A revisão não pode simplesmente
 * desaparecer — ela muda de lugar.
 *
 * `oQueMudou` é essa revisão. Antes de salvar, quem está no painel lê, em português, que vai
 * remover 12 termos e trocar a mensagem de recusa — e não "salvar alterações". Apagar uma lista
 * inteira sem ler "133 termos a menos" exige ignorar a frase, e não só errar um clique.
 */

/** Onde o documento mora. Uma coleção, um documento: o conjunto inteiro é atômico. */
export const COLECAO_DAS_REGRAS = "regras";
export const DOCUMENTO_DAS_REGRAS = "ativas";
/** E onde mora a autorização — coleção que o cliente lê sobre si e não escreve nunca. */
export const COLECAO_DOS_ADMINISTRADORES = "administradores";

export type Severidade = "critico" | "alto";
export type Categoria = "sexual" | "violencia" | "propriedade";
export type Forma = "exato" | "radical";

export interface TermoDeRegra {
  termo: string;
  forma: Forma;
  severidade: Severidade;
  categoria?: Categoria;
  contextoOk?: string[];
}

export interface FraseDeRegra {
  padrao: string;
  severidade: Severidade;
  categoria?: Categoria;
}

/**
 * T24 · UM ESTILO DE DESENHO, como o painel o edita. O operador: «na interface vai aparecer só
 * massinha, mas o que a gente envia é um prompt detalhado (...) no painel administrativo tem que
 * ter o equivalente do que é cada estilo». O `rotulo` é o que a criança vê; os três textos são o
 * que vai ao desenhista.
 */
export interface EstiloDoDesenho {
  rotulo: string;
  assuntoPersonagem: string;
  assuntoCenario: string;
  esteticaTronco: string;
}

export interface Regras {
  versao: number;
  palavras: {
    termos: TermoDeRegra[];
    frases: FraseDeRegra[];
    mensagens: Record<Categoria, string>;
  };
  desenho: {
    tetoDoPositivo: number;
    maxCena: number;
    minCena: number;
    reservaDoPersonagem: number;
    maxElenco: number;
    minElenco: number;
  };
  /**
   * O pedido de desenho. Opcional NO TIPO porque esta tela só edita os estilos dele; o resto do
   * bloco (a frase de segurança, o número de quadros) atravessa intacto, porque o conjunto é
   * gravado inteiro e o servidor confere tudo antes.
   */
  pedidoDeDesenho?: {
    esteticaTronco: string;
    assuntoPersonagem: string;
    assuntoCenario: string;
    rotuloDaCasa?: string;
    estilos?: Record<string, EstiloDoDesenho>;
    [outro: string]: unknown;
  };
  barreiraDoDesenho: {
    limiarConcentracao: number;
    limiarAltura: number;
    limiarAreaDaIlha: number;
  };
}

export const CATEGORIAS: Categoria[] = ["sexual", "violencia", "propriedade"];
export const SEVERIDADES: Severidade[] = ["critico", "alto"];
export const FORMAS: Forma[] = ["exato", "radical"];

/** Como cada categoria se chama na tela. O painel é lido por gente, não por quem escreveu o tipo. */
export const NOME_DA_CATEGORIA: Record<Categoria, string> = {
  sexual: "Conteúdo adulto e nudez",
  violencia: "Violência gráfica",
  propriedade: "Personagem e marca de outra pessoa",
};

/** E o que cada número do pedido de desenho faz, em uma linha, ao lado do campo. */
export const O_QUE_O_NUMERO_FAZ: Record<string, string> = {
  tetoDoPositivo:
    "Quantas letras o desenhista consegue ler do pedido inteiro. Acima disso, o fim do pedido é ignorado — e é no fim que ficam as palavras de segurança.",
  maxCena: "Quantas letras da frase da criança entram no pedido de desenho, no máximo.",
  minCena: "O mínimo que a frase da criança sempre tem, mesmo quando a descrição do personagem disputa espaço.",
  reservaDoPersonagem:
    "Quantas letras ficam guardadas para a descrição do personagem, para ele não trocar de cara entre os quadros.",
  maxElenco: "Quantas letras as aparências lidas automaticamente podem ocupar, somadas.",
  minElenco: "Abaixo desta folga, nenhuma aparência entra — meia descrição não descreve ninguém.",
};

export const O_QUE_O_LIMIAR_FAZ: Record<string, string> = {
  limiarConcentracao:
    "Quanto da pele do desenho precisa estar numa mancha contínua só para o desenho ser recusado. Figura vestida tem a pele partida pela roupa; figura sem roupa não.",
  limiarAltura:
    "Quanta altura do quadro essa mancha precisa atravessar. É o que separa um rosto visto bem de perto de um corpo inteiro.",
  limiarAreaDaIlha:
    "O TETO: acima deste tamanho a mancha deixa de ser uma figura e passa a ser a figura fundida com um fundo da mesma cor — porta de madeira, parede de terracota. Baixar este número recusa mais; subir recusa menos.",
};

/* ───────────────────────── a forma no banco ───────────────────────── */

/** O documento como ele é gravado. O conjunto vai num campo de texto, inteiro e de uma vez. */
export interface DocumentoDeRegras {
  versao: number;
  json: string;
  atualizadoEm: string;
  porQuem: string;
}

export function paraOBanco(regras: Regras, quem: string): DocumentoDeRegras {
  return {
    // A versão sobe sozinha a cada gravação: é o que permite dizer "o serviço está na 7 e o banco
    // na 8" quando alguém salvou e a mudança não apareceu.
    versao: regras.versao,
    json: JSON.stringify(regras),
    atualizadoEm: new Date().toISOString(),
    porQuem: quem,
  };
}

/** Lê o documento de volta. `null` quando não há nada gravado ainda, ou quando o texto não abre. */
export function doBanco(doc: unknown): Regras | null {
  const d = doc as Partial<DocumentoDeRegras> | null;
  if (!d || typeof d.json !== "string") return null;
  try {
    return JSON.parse(d.json) as Regras;
  } catch {
    return null;
  }
}

/* ───────────────────────── edições de lista ───────────────────────── */

/** Procura por texto no termo, no contexto e na categoria. 141 termos não se acham rolando. */
export function filtrarTermos(termos: TermoDeRegra[], busca: string): TermoDeRegra[] {
  const q = busca.trim().toLowerCase();
  if (!q) return termos;
  return termos.filter(
    (t) =>
      t.termo.toLowerCase().includes(q) ||
      (t.categoria ?? "sexual").includes(q) ||
      (t.contextoOk ?? []).some((c) => c.toLowerCase().includes(q))
  );
}

/** Troca um termo pelo editado, por posição — o termo pode ter mudado de texto. */
export function comOTermoTrocado(termos: TermoDeRegra[], i: number, novo: TermoDeRegra): TermoDeRegra[] {
  return termos.map((t, j) => (j === i ? novo : t));
}

export function semOTermo(termos: TermoDeRegra[], i: number): TermoDeRegra[] {
  return termos.filter((_, j) => j !== i);
}

/** O termo novo entra no TOPO: quem acabou de escrever quer vê-lo, e não procurá-lo no fim. */
export function comOTermoNovo(termos: TermoDeRegra[], novo: TermoDeRegra): TermoDeRegra[] {
  return [novo, ...termos];
}

/** Um termo em branco, para o formulário de acrescentar. */
export function termoEmBranco(): TermoDeRegra {
  return { termo: "", forma: "radical", severidade: "critico", categoria: "sexual" };
}

/* ───────────────────────── o que mudou ───────────────────────── */

export interface Mudanca {
  /** O que mudou, em português, pronto para a tela. */
  texto: string;
  /** `true` quando a mudança AFROUXA a barreira — a tela destaca essas. */
  afrouxa: boolean;
}

/**
 * Compara dois conjuntos e devolve a lista do que mudou, em português.
 *
 * O campo `afrouxa` existe porque as duas direções não têm o mesmo peso. Acrescentar um termo
 * aperta: no pior caso recusa um pedido legítimo, e alguém reclama. Remover um termo afrouxa: no
 * pior caso deixa passar o que a plataforma existe para barrar, e **ninguém reclama** — o defeito
 * sai como desenho na página de uma criança. A tela destaca o que afrouxa porque esse é o erro
 * que não se descobre sozinho.
 */
export function oQueMudou(antes: Regras, depois: Regras): Mudanca[] {
  const m: Mudanca[] = [];

  const chaveT = (t: TermoDeRegra) => `${t.termo}|${t.forma}`;
  const antesT = new Set(antes.palavras.termos.map(chaveT));
  const depoisT = new Set(depois.palavras.termos.map(chaveT));
  const saíram = antes.palavras.termos.filter((t) => !depoisT.has(chaveT(t)));
  const entraram = depois.palavras.termos.filter((t) => !antesT.has(chaveT(t)));

  if (entraram.length) {
    m.push({
      texto:
        entraram.length === 1
          ? `1 termo a mais: "${entraram[0].termo}"`
          : `${entraram.length} termos a mais: ${entraram.slice(0, 5).map((t) => `"${t.termo}"`).join(", ")}` +
            (entraram.length > 5 ? ` e mais ${entraram.length - 5}` : ""),
      afrouxa: false,
    });
  }
  if (saíram.length) {
    m.push({
      texto:
        saíram.length === 1
          ? `1 termo A MENOS: "${saíram[0].termo}" deixa de ser barrado`
          : `${saíram.length} termos A MENOS, e eles deixam de ser barrados: ` +
            saíram.slice(0, 5).map((t) => `"${t.termo}"`).join(", ") +
            (saíram.length > 5 ? ` e mais ${saíram.length - 5}` : ""),
      afrouxa: true,
    });
  }

  const antesF = new Set(antes.palavras.frases.map((f) => f.padrao));
  const depoisF = new Set(depois.palavras.frases.map((f) => f.padrao));
  const frasesQueSairam = antes.palavras.frases.filter((f) => !depoisF.has(f.padrao)).length;
  const frasesQueEntraram = depois.palavras.frases.filter((f) => !antesF.has(f.padrao)).length;
  if (frasesQueEntraram) m.push({ texto: `${frasesQueEntraram} frase(s) a mais`, afrouxa: false });
  if (frasesQueSairam) m.push({ texto: `${frasesQueSairam} frase(s) A MENOS`, afrouxa: true });

  for (const c of CATEGORIAS) {
    if (antes.palavras.mensagens[c] !== depois.palavras.mensagens[c]) {
      m.push({ texto: `a mensagem de "${NOME_DA_CATEGORIA[c]}" mudou`, afrouxa: false });
    }
  }

  for (const k of Object.keys(depois.desenho) as (keyof Regras["desenho"])[]) {
    if (antes.desenho[k] !== depois.desenho[k]) {
      m.push({ texto: `${k}: ${antes.desenho[k]} → ${depois.desenho[k]}`, afrouxa: false });
    }
  }
  // T24 · os estilos: o que a criança escolhe e o que vai ao desenhista.
  const pa = antes.pedidoDeDesenho;
  const pd = depois.pedidoDeDesenho;
  if (pa && pd) {
    if (pa.rotuloDaCasa !== pd.rotuloDaCasa) m.push({ texto: `o nome do estilo da casa: "${pa.rotuloDaCasa ?? ""}" → "${pd.rotuloDaCasa ?? ""}"`, afrouxa: false });
    for (const campo of ["esteticaTronco", "assuntoPersonagem", "assuntoCenario"] as const) {
      if (pa[campo] !== pd[campo]) m.push({ texto: `o estilo da casa mudou (${campo})`, afrouxa: false });
    }
    const ea = pa.estilos ?? {};
    const ed = pd.estilos ?? {};
    for (const tag of Object.keys(ed)) {
      if (!ea[tag]) m.push({ texto: `estilo novo: "${ed[tag].rotulo}"`, afrouxa: false });
      else if (JSON.stringify(ea[tag]) !== JSON.stringify(ed[tag])) m.push({ texto: `o estilo "${ed[tag].rotulo}" mudou`, afrouxa: false });
    }
    for (const tag of Object.keys(ea)) if (!ed[tag]) m.push({ texto: `estilo A MENOS: "${ea[tag].rotulo}" some da lista`, afrouxa: false });
  }

  for (const k of Object.keys(depois.barreiraDoDesenho) as (keyof Regras["barreiraDoDesenho"])[]) {
    const a = antes.barreiraDoDesenho[k];
    const d = depois.barreiraDoDesenho[k];
    if (a !== d) {
      // Limiar MAIOR recusa menos: é a direção que afrouxa, e é contraintuitiva o bastante para
      // merecer o destaque.
      m.push({ texto: `${k}: ${a} → ${d}`, afrouxa: d > a });
    }
  }

  return m;
}

/** Nada mudou? Então o botão de salvar não tem o que fazer, e a tela diz isso. */
export function houveMudanca(antes: Regras, depois: Regras): boolean {
  return JSON.stringify(antes) !== JSON.stringify(depois);
}
